import { useCallback } from 'react';
import { useApiData, useAuthenticatedFetch } from '../client';
import type {
  Run,
  RunCreateRequest,
  RunUpdateRequest,
  RunValidationResult,
  Publication,
  Message,
} from '../../types/media-sim';

export function useRuns(projectId: string, options?: { status?: string }) {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  const qs = params.toString();
  // Without a project id there is nothing to fetch — return an empty endpoint
  // so useApiData skips the request (avoids a 422 on /media-sim/projects//runs).
  const endpoint = !projectId
    ? ''
    : qs
    ? `/media-sim/projects/${projectId}/runs?${qs}`
    : `/media-sim/projects/${projectId}/runs`;
  return useApiData<Run[]>(endpoint);
}

export function useRun(projectId: string, runId: string) {
  return useApiData<Run>(`/media-sim/projects/${projectId}/runs/${runId}`);
}

export function useCreateRun() {
  const { fetchData } = useAuthenticatedFetch<Run>();
  const createRun = useCallback(
    (projectId: string, data: RunCreateRequest) =>
      fetchData(`/media-sim/projects/${projectId}/runs`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { createRun };
}

export function useUpdateRun() {
  const { fetchData } = useAuthenticatedFetch<Run>();
  const updateRun = useCallback(
    (projectId: string, runId: string, data: RunUpdateRequest) =>
      fetchData(`/media-sim/projects/${projectId}/runs/${runId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { updateRun };
}

export function useValidateRun() {
  const { fetchData } = useAuthenticatedFetch<RunValidationResult>();
  const validateRun = useCallback(
    (projectId: string, runId: string) =>
      fetchData(`/media-sim/projects/${projectId}/runs/${runId}/validate`, {
        method: 'POST',
      }),
    [fetchData]
  );
  return { validateRun };
}

export function useRunPublications(projectId: string, runId: string) {
  return useApiData<Publication[]>(
    `/media-sim/projects/${projectId}/runs/${runId}/publications`
  );
}

export function useSetRunPublications() {
  const { fetchData } = useAuthenticatedFetch<{ publication_ids: string[] }>();
  const setPublications = useCallback(
    (projectId: string, runId: string, publicationIds: string[]) =>
      fetchData(`/media-sim/projects/${projectId}/runs/${runId}/publications`, {
        method: 'PUT',
        body: JSON.stringify({ publication_ids: publicationIds }),
      }),
    [fetchData]
  );
  return { setPublications };
}

export function useRunMessages(projectId: string, runId: string) {
  return useApiData<Message[]>(
    `/media-sim/projects/${projectId}/runs/${runId}/messages`
  );
}

export function useSetRunMessages() {
  const { fetchData } = useAuthenticatedFetch<{ message_ids: string[] }>();
  const setMessages = useCallback(
    (projectId: string, runId: string, messageIds: string[]) =>
      fetchData(`/media-sim/projects/${projectId}/runs/${runId}/messages`, {
        method: 'PUT',
        body: JSON.stringify({ message_ids: messageIds }),
      }),
    [fetchData]
  );
  return { setMessages };
}
